import { StageOutcome } from '@lucky-vr/common';
import type { StageBriefingPayload, StageDebriefPayload } from '@lucky-vr/common';
import type { LocalScoreEntry } from './LocalProfile.js';

export class HUD {
  private menuEl = document.getElementById('menu')!;
  private hudEl = document.getElementById('hud')!;
  private statusEl = document.getElementById('status')!;
  private healthFill = document.getElementById('health-fill')!;
  private boostFill = document.getElementById('boost-fill')!;
  private scoreValue = document.getElementById('score-value')!;
  private comboEl = document.getElementById('combo')!;
  private progressFill = document.getElementById('progress-fill')!;
  private levelLabel = document.getElementById('level-label')!;
  private bossWrap = document.getElementById('boss-bar-wrap')!;
  private bossFill = document.getElementById('boss-fill')!;
  private crosshair = document.getElementById('crosshair')!;
  private vignette = document.getElementById('vignette')!;
  private popups = document.getElementById('popups')!;
  private announceEl = document.getElementById('announce')!;
  private endScreen = document.getElementById('end-screen')!;
  private endTitle = document.getElementById('end-title')!;
  private endStats = document.getElementById('end-stats')!;
  private missionScreen = document.getElementById('mission-screen')!;
  private beatTitle = document.getElementById('beat-title')!;
  private objectiveText = document.getElementById('objective-text')!;
  private timeValue = document.getElementById('time-value')!;
  private targetConfig = document.getElementById('target-config')!;
  private targetState = document.getElementById('target-state')!;
  private takedownValue = document.getElementById('takedown-value')!;
  private bossLabel = document.getElementById('boss-bar-label')!;
  private hazardWarning = document.getElementById('hazard-warning')!;
  private rankingPanel = document.getElementById('ranking-panel')!;
  private rankingList = document.getElementById('local-ranking')!;
  private rankName = document.getElementById('rank-name') as HTMLInputElement;

  private vignetteTimeout: ReturnType<typeof setTimeout> | null = null;
  private hitmarkerTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    window.addEventListener('mousemove', (e) => {
      this.crosshair.style.left = `${e.clientX}px`;
      this.crosshair.style.top = `${e.clientY}px`;
    });
  }

  showMenu(): void {
    this.menuEl.style.display = 'flex';
    this.hudEl.style.display = 'none';
    this.endScreen.style.display = 'none';
    this.missionScreen.classList.remove('show');
    this.crosshair.style.display = 'none';
    document.body.style.cursor = '';
  }

  showHUD(): void {
    this.menuEl.style.display = 'none';
    this.hudEl.style.display = 'block';
    this.endScreen.style.display = 'none';
    this.missionScreen.classList.remove('show');
    this.crosshair.style.display = 'block';
    document.body.style.cursor = 'none';
  }

  showEndScreen(
    won: boolean,
    stats: {
      score: number;
      level: number;
      time: number;
      bossEscaped?: boolean;
      debrief?: StageDebriefPayload;
    }
  ): void {
    this.endScreen.style.display = 'flex';
    this.crosshair.style.display = 'none';
    document.body.style.cursor = '';

    const debrief = stats.debrief;
    const captured = debrief?.outcome === StageOutcome.Captured;
    const campaignComplete = Boolean(debrief?.campaignComplete && captured);
    this.endTitle.textContent = !won ? 'WRECKED'
      : campaignComplete ? 'CAMPAIGN COMPLETE'
      : captured ? 'TARGET CAPTURED'
      : debrief?.outcome === StageOutcome.TimeExpired ? 'TIME EXPIRED'
      : 'TARGET ESCAPED';
    this.endTitle.className = won && captured ? 'win' : 'lose';
    const mins = Math.floor(stats.time / 60);
    const secs = Math.floor(stats.time % 60).toString().padStart(2, '0');
    this.endStats.innerHTML =
      `Score <b>${stats.score.toLocaleString()}</b><br/>` +
      `Time <b>${mins}:${secs}</b>` +
      (debrief ? `<br/>Grade <b style="color:#ffe066;font-size:1.5em">${debrief.grade}</b> · Accuracy <b>${Math.round(debrief.accuracy * 100)}%</b>` : '') +
      (debrief ? `<br/>Combat <b>${debrief.combatScore.toLocaleString()}</b> + Bonuses <b>${debrief.bonusScore.toLocaleString()}</b> − Penalties <b>${debrief.penalties.toLocaleString()}</b>` : '') +
      (debrief ? `<br/>Takedowns <b>${debrief.takedowns}</b> · Components <b>${debrief.componentsBroken}</b> · Near misses <b>${debrief.nearMisses}</b>` : '') +
      (captured && debrief?.targetBounty ? `<br/><span style="color:#2ee6d6">Bounty secured: ${debrief.targetBounty.toLocaleString()}</span>` : '') +
      (stats.bossEscaped && !debrief ? `<br/><span style="color:#ff5544">The target got away...</span>` : '') +
      (won && !campaignComplete ? `<br/>Next: <b>Operation ${stats.level + 1}</b>` : campaignComplete ? '<br/>All five operations resolved.' : '');

    const btn = document.getElementById('btn-again')!;
    btn.textContent = campaignComplete ? 'New Campaign' : won ? 'Next Operation' : 'Try Again';
    this.rankingPanel.classList.toggle('show', campaignComplete);
  }

  hideEndScreen(): void {
    this.endScreen.style.display = 'none';
    this.rankingPanel.classList.remove('show');
  }

  showBriefing(data: StageBriefingPayload): void {
    this.menuEl.style.display = 'none';
    this.hudEl.style.display = 'none';
    this.endScreen.style.display = 'none';
    this.missionScreen.classList.add('show');
    document.getElementById('mission-kicker')!.textContent =
      `OPERATION ${String(data.level).padStart(2, '0')} // BRIEFING`;
    document.getElementById('mission-title')!.textContent = data.title.toUpperCase();
    document.getElementById('mission-location')!.textContent = data.location.toUpperCase();
    document.getElementById('mission-briefing')!.textContent = data.briefing;
    document.getElementById('mission-target')!.textContent = data.targetTitle;
    document.getElementById('mission-bounty')!.textContent = data.targetBounty.toLocaleString();
    document.getElementById('mission-time')!.textContent = this.formatTime(data.timeLimitSeconds);
    (document.getElementById('btn-skip') as HTMLButtonElement).textContent = 'VOTE READY';
    (document.getElementById('btn-skip') as HTMLButtonElement).disabled = false;
    document.body.style.cursor = '';
  }

  hideBriefing(): void {
    this.missionScreen.classList.remove('show');
  }

  updateBriefingVotes(votes: number, required: number): void {
    const button = document.getElementById('btn-skip') as HTMLButtonElement;
    button.textContent = required > 1 ? `READY ${votes}/${required}` : 'VOTE READY';
  }

  updateMission(data: {
    beatTitle: string;
    objective: string;
    timeRemaining: number;
    targetTitle: string;
    targetConfiguration: string;
    targetState: string;
    takedowns: number;
  }): void {
    this.beatTitle.textContent = data.beatTitle.toUpperCase();
    this.objectiveText.textContent = data.objective;
    this.timeValue.textContent = this.formatTime(data.timeRemaining);
    this.targetConfig.textContent = (data.targetConfiguration || 'NO CONTACT').replaceAll('_', ' ').toUpperCase();
    this.targetState.textContent = (data.targetState || 'UNKNOWN').replaceAll('_', ' ').toUpperCase();
    this.takedownValue.textContent = String(data.takedowns);
    this.bossLabel.textContent = data.targetTitle || 'TARGET';
  }

  warnHazard(label: string, committed = false): void {
    this.hazardWarning.textContent = `${committed ? '⛔ MOVE NOW' : '⚠ HAZARD'} // ${label}`;
    this.hazardWarning.classList.add('show');
    this.hazardWarning.style.borderColor = committed ? '#ff3322' : '#ffb020';
  }

  clearHazard(hit: boolean, nearMiss: boolean): void {
    this.hazardWarning.classList.remove('show');
    if (nearMiss) this.announce('NEAR MISS +250');
    else if (!hit) this.announce('HAZARD EVADED');
  }

  setTargetLabel(label: string): void {
    this.bossLabel.textContent = label;
  }

  showLocalRanking(entries: LocalScoreEntry[], agentName: string, banked = false): void {
    this.rankingPanel.classList.add('show');
    this.rankName.value = agentName;
    this.rankName.disabled = banked;
    const bankButton = document.getElementById('btn-bank') as HTMLButtonElement;
    bankButton.disabled = banked;
    bankButton.textContent = banked ? 'BANKED' : 'BANK SCORE';
    this.rankingList.replaceChildren();
    entries.forEach((entry, index) => {
      const row = document.createElement('li');
      for (const value of [
        `${index + 1}.`,
        entry.name,
        entry.grade,
        entry.score.toLocaleString(),
      ]) {
        const cell = document.createElement('span');
        cell.textContent = value;
        row.appendChild(cell);
      }
      this.rankingList.appendChild(row);
    });
  }

  private formatTime(seconds: number): string {
    const safe = Math.max(0, Math.ceil(seconds));
    return `${Math.floor(safe / 60).toString().padStart(2, '0')}:${(safe % 60).toString().padStart(2, '0')}`;
  }

  /** Optional mirror (the VR menu shows status in-world). */
  onStatus: ((text: string) => void) | null = null;

  setStatus(text: string): void {
    this.statusEl.textContent = text;
    this.onStatus?.(text);
  }

  announce(text: string): void {
    this.announceEl.textContent = text;
    this.announceEl.classList.remove('show');
    // restart the CSS animation
    void this.announceEl.offsetWidth;
    this.announceEl.classList.add('show');
  }

  updateHealth(health: number, maxHealth: number): void {
    const pct = Math.max(0, (health / maxHealth) * 100);
    this.healthFill.style.width = `${pct}%`;
    this.healthFill.style.background =
      pct > 60 ? 'linear-gradient(90deg, #51cf66, #2f9e44)'
      : pct > 30 ? 'linear-gradient(90deg, #fcc419, #f59f00)'
      : 'linear-gradient(90deg, #ff6b6b, #e03131)';
  }

  updateBoost(charge: number, maxCharge: number): void {
    const pct = Math.max(0, Math.min(100, (charge / maxCharge) * 100));
    this.boostFill.style.width = `${pct}%`;
    this.boostFill.style.background = pct < 20
      ? '#ff5544'
      : 'linear-gradient(90deg, #2ee6d6, #ff2d95)';
  }

  updateScore(score: number): void {
    this.scoreValue.textContent = score.toLocaleString();
  }

  updateCombo(combo: number): void {
    this.comboEl.textContent = `×${combo}`;
    this.comboEl.classList.toggle('active', combo > 1);
  }

  updateProgress(t: number): void {
    this.progressFill.style.width = `${Math.min(100, t * 100)}%`;
  }

  setLevelLabel(level: number, theme: string): void {
    this.levelLabel.textContent = `LEVEL ${level} — ${theme.toUpperCase()}`;
  }

  /** Tint the countdown/announcements and progress bar in the level's livery. */
  setThemeAccent(primary: string, secondary: string): void {
    this.announceEl.style.color = primary;
    this.announceEl.style.textShadow =
      `4px 4px 0 ${secondary}, 0 0 40px ${primary}b0, 0 4px 12px rgba(0,0,0,0.9)`;
    this.levelLabel.style.color = primary;
    this.progressFill.style.background = `linear-gradient(90deg, ${primary}, ${secondary})`;
  }

  showBossBar(visible: boolean): void {
    this.bossWrap.style.display = visible ? 'block' : 'none';
  }

  updateBossHealth(health: number, maxHealth: number): void {
    this.bossFill.style.width = `${Math.max(0, (health / maxHealth) * 100)}%`;
  }

  flashVignette(): void {
    this.vignette.style.opacity = '1';
    if (this.vignetteTimeout) clearTimeout(this.vignetteTimeout);
    this.vignetteTimeout = setTimeout(() => {
      this.vignette.style.opacity = '0';
    }, 180);
  }

  flashHitmarker(): void {
    this.crosshair.classList.add('hitmarker');
    if (this.hitmarkerTimeout) clearTimeout(this.hitmarkerTimeout);
    this.hitmarkerTimeout = setTimeout(() => {
      this.crosshair.classList.remove('hitmarker');
    }, 90);
  }

  /** Floating score popup at a screen position (px). */
  scorePopup(x: number, y: number, text: string, big = false): void {
    const el = document.createElement('div');
    el.className = 'popup';
    el.textContent = text;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.fontSize = big ? '2rem' : '1.3rem';
    this.popups.appendChild(el);
    setTimeout(() => el.remove(), 950);
  }

  /** Small per-shot damage number at a screen position (px). */
  damagePopup(x: number, y: number, damage: number): void {
    const el = document.createElement('div');
    el.className = 'popup dmg';
    el.textContent = String(damage);
    // slight scatter so rapid fire doesn't stack into one blob
    el.style.left = `${x + (Math.random() - 0.5) * 30}px`;
    el.style.top = `${y + (Math.random() - 0.5) * 16}px`;
    this.popups.appendChild(el);
    setTimeout(() => el.remove(), 650);
  }
}
